import { useSyncExternalStore } from "react";
import { cantidadInicial, escalarLinea } from "../components/Recipes/escalarIngredientes";

// ══════════════════════════════════════════════════════
//  LISTA DE LA COMPRA
//  Vive en el navegador (localStorage), no en la base de datos: no hace falta cuenta.
//  Forma guardada: { recetas: [{ id, slug, nombre, raciones, lineas: [texto plano] }], marcados: [clave] }
//  Un almacén de módulo + useSyncExternalStore mantiene al día todos los componentes y las pestañas.
//  Sin localStorage (modo privado) la lista dura lo que dure la pestaña.
// ══════════════════════════════════════════════════════

const CLAVE = "plato:compra";
const VACIA = { recetas: [], marcados: [] };

const normalizarLista = (crudo) => {
  const recetas = Array.isArray(crudo?.recetas)
    ? crudo.recetas
        .filter((r) => r && typeof r.id === "string" && Array.isArray(r.lineas))
        .map((r) => ({
          id: r.id,
          slug: typeof r.slug === "string" ? r.slug : "",
          nombre: String(r.nombre || "Receta"),
          raciones: Number(r.raciones) || 1,
          lineas: r.lineas.map(String).filter(Boolean),
        }))
    : [];
  const marcados = Array.isArray(crudo?.marcados) ? crudo.marcados.map(String) : [];
  return { recetas, marcados };
};

const leer = () => {
  try {
    return normalizarLista(JSON.parse(window.localStorage.getItem(CLAVE) || "null"));
  } catch {
    return VACIA;
  }
};

let estado = typeof window === "undefined" ? VACIA : leer();
const oyentes = new Set();

const avisar = () => oyentes.forEach((f) => f());

const escribir = (nuevo) => {
  estado = nuevo;
  try {
    window.localStorage.setItem(CLAVE, JSON.stringify(nuevo));
  } catch {
    /* sin almacenamiento: queda en memoria */
  }
  avisar();
};

// Cambios hechos desde otra pestaña
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === CLAVE || e.key === null) {
      estado = leer();
      avisar();
    }
  });
}

const suscribir = (f) => {
  oyentes.add(f);
  return () => oyentes.delete(f);
};

// ── Fusionar: la misma línea de varias recetas se suma ─────────────
const normalizar = (t) =>
  t
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9 ]+/g, " ")
    .split(/\s+/)
    .filter((p) => p && !["de", "del", "la", "el", "los", "las"].includes(p))
    .map((p) => (p.length > 3 && p.endsWith("s") ? p.slice(0, -1) : p))
    .join(" ");

// → [{ clave, texto, de: [nombres de receta] }] en orden de aparición
export const fusionar = (recetas) => {
  const grupos = new Map();
  recetas.forEach((receta) => {
    receta.lineas.forEach((linea) => {
      const cant = cantidadInicial(linea);
      const clave = cant ? `c:${normalizar(cant.resto)}` : `t:${normalizar(linea)}`;
      if (!clave.slice(2)) return;
      let g = grupos.get(clave);
      if (!g) {
        g = { clave, primera: linea, primero: cant?.valor ?? null, suma: cant?.valor ?? null, de: [] };
        grupos.set(clave, g);
      } else if (cant && g.suma !== null) {
        g.suma += cant.valor;
      }
      if (!g.de.includes(receta.nombre)) g.de.push(receta.nombre);
    });
  });
  return [...grupos.values()].map((g) => ({
    clave: g.clave,
    // La suma reutiliza el ajuste de raciones: formatea la cantidad y concuerda singular/plural
    texto: g.suma !== null && g.suma !== g.primero ? escalarLinea(g.primera, g.suma / g.primero).texto : g.primera,
    de: g.de,
  }));
};

// ── Acciones ───────────────────────────────────────────────────────
export const añadirReceta = (receta) => {
  const resto = estado.recetas.filter((r) => r.id !== receta.id);
  escribir({ ...estado, recetas: [...resto, receta] });
};

export const quitarReceta = (id) => {
  const recetas = estado.recetas.filter((r) => r.id !== id);
  const vivas = new Set(fusionar(recetas).map((i) => i.clave));
  escribir({ recetas, marcados: estado.marcados.filter((k) => vivas.has(k)) });
};

export const alternarMarcado = (clave) => {
  const marcados = estado.marcados.includes(clave) ? estado.marcados.filter((k) => k !== clave) : [...estado.marcados, clave];
  escribir({ ...estado, marcados });
};

export const quitarMarcados = () => {
  const items = fusionar(estado.recetas).filter((i) => !estado.marcados.includes(i.clave));
  // Las líneas marcadas se sacan de cada receta; una receta sin líneas desaparece
  const vivas = new Set(items.map((i) => i.clave));
  const recetas = estado.recetas
    .map((r) => ({
      ...r,
      lineas: r.lineas.filter((l) => {
        const c = cantidadInicial(l);
        return vivas.has(c ? `c:${normalizar(c.resto)}` : `t:${normalizar(l)}`);
      }),
    }))
    .filter((r) => r.lineas.length > 0);
  escribir({ recetas, marcados: [] });
};

export const vaciarLista = () => escribir(VACIA);

export const useListaCompra = () =>
  useSyncExternalStore(
    suscribir,
    () => estado,
    () => VACIA,
  );
